/**
 * Date utilities to handle timezone-safe date formatting
 * 
 * PROBLEM: When parsing date strings like "2026-03-02", JavaScript interprets them
 * as midnight UTC. When displaying in local timezone (e.g., EST), this can show
 * the previous day (March 1 instead of March 2).
 * 
 * SOLUTION: Parse the date string manually to avoid timezone conversion issues.
 * 
 * USAGE: Import and use these functions EVERYWHERE dates are displayed.
 * NEVER use: new Date(dateString).toLocaleDateString()
 * ALWAYS use: formatDateSafe(dateString) or parseLocalDate(dateString)
 */

/**
 * Parse a YYYY-MM-DD date string as LOCAL time (not UTC)
 * Use this instead of new Date(dateString) which parses as UTC
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @returns {Date} Date object in local timezone
 */
export const parseLocalDate = (dateString) => {
  if (!dateString) return new Date();
  
  // Handle different formats
  if (dateString.includes('T')) {
    // ISO format with time - append Z if not present to force UTC, then convert
    return new Date(dateString);
  }
  
  // YYYY-MM-DD format - parse manually to avoid UTC interpretation
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const [year, month, day] = parts.map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed
  }
  
  // Fallback: try replacing dashes with slashes (also parses as local)
  return new Date(dateString.replace(/-/g, '/'));
};

/**
 * Convert a Date object to a LOCAL YYYY-MM-DD string (no UTC shift).
 * Use this instead of date.toISOString().split('T')[0], which converts to UTC
 * and can roll the calendar day forward/back for non-UTC timezones.
 * @param {Date} date
 * @returns {string} YYYY-MM-DD in the browser's local timezone
 */
export const toLocalYMD = (date) => {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Format a date string (YYYY-MM-DD) for display without timezone conversion
 * @param {string} dateString - Date in YYYY-MM-DD format
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDateSafe = (dateString, options = {}) => {
  if (!dateString) return '';
  
  const date = parseLocalDate(dateString);
  
  const defaultOptions = {
    year: 'numeric',
    month: 'numeric', 
    day: 'numeric',
    ...options
  };
  
  return date.toLocaleDateString('en-US', defaultOptions);
};

/**
 * Format a date string for short display (M/D/YYYY)
 */
export const formatDateShort = (dateString) => {
  return formatDateSafe(dateString);
};

/**
 * Format a date string for long display (Month D, YYYY)
 */
export const formatDateLong = (dateString) => {
  return formatDateSafe(dateString, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Format a date string with weekday (Mon, Mar 2)
 */
export const formatDateWithWeekday = (dateString) => {
  return formatDateSafe(dateString, {
    weekday: 'short',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format a date range (handles same-day and multi-day ranges)
 */
export const formatDateRange = (startDate, endDate) => {
  const start = formatDateSafe(startDate);
  const end = formatDateSafe(endDate);
  
  if (startDate === endDate) {
    return start;
  }
  
  return `${start} - ${end}`;
};
